import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Typography, Avatar, Space, Button, Upload, Tooltip, message,
  Form, Input, Spin, Select, DatePicker, Row, Col, Tag,
  Progress, Modal, Grid
} from 'antd';
import {
  UserOutlined, EditOutlined, DeleteOutlined, CrownOutlined,
  MailOutlined, PhoneOutlined, CalendarOutlined, HomeOutlined,
  SaveOutlined, CloseOutlined, IdcardOutlined,
  TeamOutlined, SettingOutlined,
  ClockCircleOutlined, CheckCircleOutlined,
  InfoCircleOutlined, KeyOutlined,
  EnvironmentOutlined, BankOutlined, ShopOutlined,
  GlobalOutlined, SafetyCertificateOutlined, StarOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Gender } from '../../actions/constants/constant';
import dayjs from 'dayjs';
import { commonActions } from '../../actions/commonActions';
import { userActions } from '../../actions/userActions';
import SpinnerLoader from '../../components/spinnerLoader';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface ProfileProps {
  role: string;
}

/* ── reusable info-field ── */
const InfoField: React.FC<{ label: string; value?: string; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="pv2-field">
    {icon && <span className="pv2-field-icon">{icon}</span>}
    <div className="pv2-field-body">
      <span className="pv2-field-label">{label}</span>
      <span className="pv2-field-value">{value || '—'}</span>
    </div>
  </div>
);

/* ── section card ── */
const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onEdit?: () => void;
  editing?: boolean;
}> = ({ title, icon, children, onEdit, editing }) => (
  <div className="pv2-section-card">
    <div className="pv2-section-head">
      <div className="pv2-section-head-left">
        <span className="pv2-section-icon">{icon}</span>
        <span className="pv2-section-title">{title}</span>
      </div>
      {onEdit && !editing && (
        <button className="pv2-section-edit-btn" onClick={onEdit} aria-label={`Edit ${title}`}>
          <EditOutlined />
        </button>
      )}
    </div>
    <div className="pv2-section-body">{children}</div>
  </div>
);

const Profile: React.FC<ProfileProps> = ({ role }) => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  const auth = useSelector((state: any) => state.authReducer);
  const profile = useSelector((state: any) => state.getProfileReducer);
  const updateprofile = useSelector((state: any) => state.updateProfileReducer);
  const uploadState = useSelector((state: any) => state.updateProfileImageReducer);
  const deleteFilesState = useSelector((state: any) => state.deleteFilesReducer);
  const user = useMemo(() => auth?.authData ?? {}, [auth?.authData]);

  const data = localStorage.getItem('AUTHDATA');
  const profileData = JSON.parse(String(data));

  const profileValues = useMemo(() => {
    const p = profile?.profileData ?? {};

    // ── subscription ──────────────────────────────────────────────
    const sub = p.currentSubscription || p.subscription || p.userSubscription || {};
    const plan = sub?.plan || sub?.subscriptionPlan || sub?.plan_id || {};

    // ── tenant ────────────────────────────────────────────────────
    const tenant = p.tenant || {};

    // ── preference ────────────────────────────────────────────────
    const pref = p.preference || {};

    return {
      /* personal */
      firstName: p.firstName || '—',
      lastName: p.lastName || '—',
      gender: p.gender || '—',
      email: p.email || '—',
      phone: p.phoneNumber || p.phone || '—',
      address: p.address || '—',
      dateOfBirth: p.dateOfBirth || '—',
      isActive: p.isActive !== false,

      /* role / account */
      roleName: p.role?.roleName || '—',
      roleCode: p.role?.role || '—',
      lastLogin: p.lastLoginAt || p.lastLogin || null,
      createdAt: p.createdAt || null,
      updatedAt: p.updatedAt || null,
      usedStorage: p.usedStorage ?? 0,
      status: p.status || '—',

      /* tenant / organisation */
      tenantName: p.tenantName || tenant?.name || '—',
      tenantEmail: tenant?.email || '—',
      tenantPhone: tenant?.phoneNumber || '—',
      tenantAddress: tenant?.address || '—',
      tenantCountry: tenant?.country || '—',
      branchCount: tenant?.branchCount ?? '—',
      isBranchEnabled: tenant?.isBranchEnabled ? 'Yes' : 'No',
      validityStart: tenant?.validityStartDate || null,
      validityEnd: tenant?.validityEndDate || null,

      /* available tenants */
      availableTenants: p.availableTenants || [],

      /* preferences */
      otpExpiry: pref?.otpExpiryTime || '—',
      cooldown: pref?.cooldownTime || '—',
      webTokenLife: pref?.webTokenLifetime || '—',
      mobileTokenLife: pref?.mobileTokenLifetime || '—',
      inviteTokenLife: pref?.invitationTokenLifetime || '—',

      /* subscription */
      planName: sub?.planName || plan?.name || plan?.planName || 'No Plan',
      planPrice: plan?.price ?? plan?.amount ?? 0,
      planStatus: (sub?.status || '').toString().toLowerCase() || 'inactive',
      planStartDate: sub?.startDate || sub?.createdAt || null,
      planEndDate: sub?.endDate || sub?.expiryDate || null,
      billingCycle: sub?.billingCycle || plan?.billingCycle || 'monthly',
      amountPaid: sub?.amountPaid ?? 0,
      autoRenewal: sub?.autoRenewal ? 'Yes' : 'No',
      planContent: plan?.content || [],
      branchLimit: plan?.branchLimit ?? '—',
      storageLimit: plan?.storageLimit ?? '—',

      raw: p,
    };
  }, [profile]);

  const initialAvatar = useMemo(() => {
    if (user?.avatarUrl || user?.photo || user?.profilePictureUrl)
      return user.avatarUrl || user.photo || user.profilePictureUrl;
    const p = profile?.profileData;
    if (p?.profileImageUrl) return p.profileImageUrl;
    const img = p?.profileImage;
    if (typeof img === 'string') return img;
    if (img?.url) return img.url;
    if (img?.buffer) return `data:${img.mimetype || 'image/png'};base64,${img.buffer}`;
    return '';
  }, [user, profile?.profileData]);

  const [avatarUrl, setAvatarUrl] = useState<string>(initialAvatar);
  const [editingInitialAvatar, setEditingInitialAvatar] = useState<string>(initialAvatar);
  const [editing, setEditing] = useState(false);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [form] = Form.useForm();
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);

  // Tracks the last processed upload so repeat uploads are never skipped
  const lastHandledUploadRef = useRef<string | null>(null);
  // Forces the browser to re-fetch the image instead of using its cache
  const withCacheBuster = (url: string) => {
    if (!url || url.startsWith('data:')) return url;
    return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  };

  const onEditStart = () => {
    setEditingInitialAvatar(avatarUrl);
    setImageDeleted(false);
    setEditing(true);
  };

  const onEditCancel = () => {
    
    setAvatarUrl(editingInitialAvatar);
    setImageDeleted(false);
    setEditing(false);
  };

  const handleDeleteImage = () => {
    if (uploadedImageId) {
      dispatch(commonActions.deleteFiles({ ids: [uploadedImageId] }));
      setUploadedImageId(null);
    } else {
      const currentImage = profile?.profileData?.profileImage;
      let fileId: string | null = null;
      if (currentImage) {
        if (typeof currentImage === 'string') fileId = currentImage;
        else if ((currentImage as any)._id) fileId = (currentImage as any)._id;
        else if ((currentImage as any).fileId) fileId = (currentImage as any).fileId;
      }
      if (fileId) dispatch(commonActions.deleteFiles({ ids: [fileId] }));
    }
    setAvatarUrl('');
    setImageDeleted(true);
    setEditing(false);
    setUploadedImageId(null);
  };

  const beforeUpload = (file: File) => {
    if (!file.type.startsWith('image/')) return Upload.LIST_IGNORE;
    if (file.size / 1024 / 1024 >= 3) { message.error('Image must be smaller than 3MB'); return Upload.LIST_IGNORE; }

    // Optimistic local preview so the user always sees the new image immediately
    const localPreview = URL.createObjectURL(file);
    setAvatarUrl(localPreview);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'userProfile');
    formData.append('ReferenceId', String(user?.id || profile?.profileData?._id || ''));
    dispatch(userActions.uploadProfileImageRequest(formData));
    return false;
  };

  useEffect(() => {
    if (editing) {
      form.setFieldsValue({
        firstName: profileValues.firstName !== '—' ? profileValues.firstName : '',
        phone: profileValues.phone !== '—' ? profileValues.phone : '',
        address: profileValues.address !== '—' ? profileValues.address : '',
        lastName: profileValues.lastName !== '—' ? profileValues.lastName : '',
        gender: profileValues.gender !== '—' ? profileValues.gender : undefined,
        dateOfBirth: profileValues.dateOfBirth !== '—' ? dayjs(profileValues.dateOfBirth) : undefined,
      });
    } else { form.resetFields(); }
  }, [editing, form, profileValues]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const updatedUser: any = {
        firstName: values.firstName,
        address: values.address,
        lastName: values.lastName,
        gender: values.gender,
        dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format('YYYY-MM-DD') : undefined,
      };
      if (!imageDeleted && uploadedImageId) {
        updatedUser.profileImage = uploadedImageId;
      }
      dispatch(userActions.updateProfileRequest(updatedUser));
      setEditing(false);
      setImageDeleted(false);
      setUploadedImageId(null);
    } catch { }
  };

  useEffect(() => { dispatch(userActions.getProfileRequest({ id: profileData.id })); }, [dispatch]);

  useEffect(() => {
    if (!profile?.isSuccess) return;
    if (uploadState?.isLoading) return;

    const p = profile?.profileData;
    const imgUrl =
      p?.profileImageUrl ||
      p?.profileImage?.url ||
      (p?.profileImage?.buffer
        ? `data:${p.profileImage.mimetype || 'image/png'};base64,${p.profileImage.buffer}`
        : '') ||
      initialAvatar;

    if (imgUrl) {
      const busted = withCacheBuster(imgUrl);
      setAvatarUrl(busted);
      setEditingInitialAvatar(busted);
    }
  }, [profile?.isSuccess, profile?.profileData, uploadState?.isLoading, initialAvatar]);

  useEffect(() => {
    if (updateprofile?.isSuccess) dispatch(userActions.getProfileRequest({ id: profileData.id }));
  }, [updateprofile, dispatch]);

  useEffect(() => {
    if (!uploadState) return;
    if (uploadState.isError) {
      setAvatarUrl(editing ? editingInitialAvatar : initialAvatar || '');
      setImageDeleted(false);
      lastHandledUploadRef.current = null;
      return;
    }
    if (uploadState.isSuccess && uploadState.profileImage) {
      const result = uploadState.profileImage;
      const imageId = result._id || null;
      const signature = `${imageId || ''}|${result.url || ''}|${result.updatedAt || ''}`;

      // Guard against re-processing the same payload, but always allow a NEW upload
      if (lastHandledUploadRef.current === signature) return;
      lastHandledUploadRef.current = signature;

      if (result.url) {
        const busted = withCacheBuster(result.url);
        setAvatarUrl(busted);
        setEditingInitialAvatar(busted);
      }

      if (imageId) {
        setUploadedImageId(imageId);
        setEditing(false);
        setImageDeleted(false);
      }

      dispatch(userActions.getProfileRequest({ id: profileData.id }));
    }
  }, [uploadState?.isSuccess, uploadState?.isError, uploadState?.profileImage, dispatch]);

  useEffect(() => {
    if (deleteFilesState?.isSuccess) {
      dispatch(userActions.getProfileRequest({ id: profileData.id }));
    }
  }, [deleteFilesState?.isSuccess, dispatch]);

  /* ── computed (must be after all hooks) ── */
  const fullName = `${profileValues.firstName} ${profileValues.lastName}`.trim().replace(/^—\s—$/, '—');

  const subProgress = useMemo(() => {
    if (!profileValues.planStartDate || !profileValues.planEndDate) return 0;
    const total = Math.max(dayjs(profileValues.planEndDate).diff(dayjs(profileValues.planStartDate), 'day'), 1);
    const elapsed = Math.max(dayjs().diff(dayjs(profileValues.planStartDate), 'day'), 0);
    return Math.min(Math.round((elapsed / total) * 100), 100);
  }, [profileValues.planStartDate, profileValues.planEndDate]);

  if (profile?.isLoading || updateprofile?.isLoading) return <SpinnerLoader />;

  return (
    <div className="pv2-page">

      {/* ── Hero card ── */}
      <div className="pv2-hero-card">

        {/* Avatar */}
        <div className="pv2-hero-avatar-col">
          <div
            className="pv2-avatar-wrap"
            onClick={() => {
              if (avatarUrl && !editing)
                Modal.info({
                  title: 'Profile Image', width: 460, icon: null,
                  content: <div className="profile-image-modal"><img src={avatarUrl} alt="Profile" style={{ maxWidth: '100%', height: 380, borderRadius: 8 }} /></div>,
                  okText: 'Close',
                });
            }}
          >
            <Spin spinning={!!uploadState?.isLoading}>
              <Avatar
                key={avatarUrl}
                size={90}
                icon={<UserOutlined />}
                src={avatarUrl || undefined}
                className="pv2-avatar"
              />
            </Spin>
            {!editing && avatarUrl && <div className="pv2-avatar-overlay"><EditOutlined /></div>}
          </div>
          {editing && (
            <div className="pv2-avatar-edit-actions">
              <Upload accept="image/*" showUploadList={false} beforeUpload={beforeUpload}>
                <Tooltip title="Upload photo"><Button size="small" shape="circle" icon={<EditOutlined />} /></Tooltip>
              </Upload>
              {avatarUrl && (
                <Tooltip title="Remove">
                  <Button size="small" shape="circle" danger icon={<DeleteOutlined />} onClick={handleDeleteImage} />
                </Tooltip>
              )}
            </div>
          )}
        </div>

        {/* Identity */}
        <div className="pv2-hero-identity">
          <div className="pv2-hero-name-row">
            <h1 className="pv2-hero-name">{fullName}</h1>
            <span className={`pv2-status-badge ${profileValues.isActive ? 'pv2-status-badge--active' : 'pv2-status-badge--inactive'}`}>
              <span className="pv2-status-dot" />
              {profileValues.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="pv2-hero-meta-row">
            <Tag color="blue" className="pv2-role-tag">{profileValues.roleName}</Tag>
            <span className="pv2-hero-dept"><ShopOutlined /> {profileValues.tenantName}</span>
            {profileValues.createdAt && (
              <span className="pv2-hero-dept"><CalendarOutlined /> Joined {dayjs(profileValues.createdAt).format('MMM YYYY')}</span>
            )}
          </div>
          <div className="pv2-hero-contacts">
            <span className="pv2-hero-contact-item"><MailOutlined />{profileValues.email}</span>
            <span className="pv2-hero-contact-item"><PhoneOutlined />{profileValues.phone}</span>
            {profileValues.address !== '—' && (
              <span className="pv2-hero-contact-item"><EnvironmentOutlined />{profileValues.address}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="pv2-hero-actions">
          {!editing ? (
            <Button type="primary" icon={<EditOutlined />} onClick={onEditStart} className="pv2-edit-btn">
              Edit Profile
            </Button>
          ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={onEditCancel}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={updateprofile?.isLoading}>
                Save Changes
              </Button>
            </Space>
          )}
        </div>
      </div>

      {/* ── Edit Form ── */}
      {editing && (
        <div className="pv2-edit-form-card">
          <div className="pv2-section-head">
            <div className="pv2-section-head-left">
              <span className="pv2-section-icon"><UserOutlined /></span>
              <span className="pv2-section-title">Edit Personal Details</span>
            </div>
          </div>
          <div className="pv2-section-body">
            <Form form={form} layout="vertical">
              <Row gutter={[20, 0]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                    <Input placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                    <Input placeholder="Last Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="phone" label="Phone"
                    rules={[{ required: true }, { pattern: /^\d{10}$/, message: '10-digit number required' }]}>
                    <Input placeholder="10-digit mobile" maxLength={10} addonBefore="+91" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
                    <Select placeholder="Select gender">
                      {Gender.map((g: string) => <Select.Option key={g} value={g}>{g}</Select.Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="dateOfBirth" label="Date of Birth" rules={[{ required: true }]} >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      disabledDate={(current) => current && current.isAfter(dayjs().subtract(18, 'year'))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={16}>
                  <Form.Item name="address" label="Address" rules={[{ required: true }]}>
                    <Input.TextArea rows={2} placeholder="Street, City, State, PIN" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      )}

      {/* ── Info Grid ── */}
      {!editing && (
        <div className="pv2-grid">

          {/* ── Card 1 : Personal Information ── */}
          <SectionCard title="Personal Information" icon={<UserOutlined />}>
            <div className="pv2-fields-grid">
              <InfoField label="First Name" value={profileValues.firstName} />
              <InfoField label="Last Name" value={profileValues.lastName} />
              <InfoField label="Gender" value={profileValues.gender} icon={<UserOutlined />} />
              <InfoField
                label="Date of Birth"
                value={profileValues.dateOfBirth !== '—' ? dayjs(profileValues.dateOfBirth).format('DD MMM YYYY') : '—'}
                icon={<CalendarOutlined />}
              />
              <InfoField label="Phone" value={profileValues.phone} icon={<PhoneOutlined />} />
              <InfoField label="Email" value={profileValues.email} icon={<MailOutlined />} />
              <div className="pv2-field pv2-field--full">
                <span className="pv2-field-icon"><HomeOutlined /></span>
                <div className="pv2-field-body">
                  <span className="pv2-field-label">Address</span>
                  <span className="pv2-field-value">{profileValues.address}</span>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── Card 2 : Organisation Information ── */}
        {role === 'ADMIN' && (
          <SectionCard title="Organisation Information" icon={<ShopOutlined />}>
            <div className="pv2-fields-grid">
              <InfoField label="Organisation" value={profileValues.tenantName} icon={<ShopOutlined />} />
              <InfoField label="Email" value={profileValues.tenantEmail} icon={<MailOutlined />} />
              <InfoField label="Phone" value={profileValues.tenantPhone} icon={<PhoneOutlined />} />
              <InfoField label="Country" value={profileValues.tenantCountry} icon={<GlobalOutlined />} />
              <InfoField label="Branch Count" value={String(profileValues.branchCount)} icon={<BankOutlined />} />
              {profileValues.validityStart && (
                <InfoField
                  label="Validity Start"
                  value={dayjs(profileValues.validityStart).format('DD MMM YYYY')}
                  icon={<CalendarOutlined />}
                />
              )}
              {profileValues.validityEnd && (
                <InfoField
                  label="Validity End"
                  value={dayjs(profileValues.validityEnd).format('DD MMM YYYY')}
                  icon={<CalendarOutlined />}
                />
              )}
              
              {profileValues.availableTenants.length > 0 && (
                <div className="pv2-field pv2-field--full">
                  <span className="pv2-field-icon"><TeamOutlined /></span>
                  <div className="pv2-field-body">
                    <span className="pv2-field-label">Accessible Organisations</span>
                    <div className="pv2-tenant-tags">
                      {profileValues.availableTenants.map((t: any) => (
                        <Tag key={t._id} color="blue" style={{ marginTop: 4 }}>{t.name}</Tag>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </SectionCard>)}

          {/* ── Card 3 : Account & System ── */}
          <SectionCard title="Account & System" icon={<SettingOutlined />}>
            <div className="pv2-fields-grid">
              <InfoField label="Role" value={profileValues.roleName} icon={<IdcardOutlined />} />
              
              <InfoField
                label="Account Status"
                value={profileValues.isActive ? 'Active' : 'Inactive'}
                icon={<CheckCircleOutlined />}
              />
              <InfoField label="Status" value={profileValues.status} icon={<StarOutlined />} />
              <InfoField
                label="Last Login"
                value={profileValues.lastLogin ? dayjs(profileValues.lastLogin).format('DD MMM YYYY HH:mm') : '—'}
                icon={<ClockCircleOutlined />}
              />
              <InfoField
                label="Last Updated"
                value={profileValues.updatedAt ? dayjs(profileValues.updatedAt).format('DD MMM YYYY') : '—'}
                icon={<InfoCircleOutlined />}
              />
              <InfoField
                label="Account Created"
                value={profileValues.createdAt ? dayjs(profileValues.createdAt).format('DD MMM YYYY') : '—'}
                icon={<KeyOutlined />}
              />
             
            </div>
          </SectionCard>

        

          {/* ── Card 5 : Subscription — ADMIN only ── */}
          {role === 'ADMIN' && (
            <SectionCard title="Subscription Plan" icon={<CrownOutlined />}>
              <div className="pv2-sub-card">

                {/* Plan header */}
                <div className="pv2-sub-plan-row">
                  <div className="pv2-sub-plan-info">
                    <span className="pv2-sub-plan-name">{profileValues.planName}</span>
                    <span className="pv2-sub-plan-price">
                      ₹ {profileValues.planPrice.toFixed(2)}
                      <span className="pv2-sub-plan-cycle"> / {profileValues.billingCycle}</span>
                    </span>
                  </div>
                  <div className="pv2-sub-plan-right">
                    <Tag style={{border:"1px solid #eaeaea"}}
                      color={profileValues.planStatus === 'active' ? 'success' : 'default'}
                      className="pv2-sub-status-tag"
                    >
                      {profileValues.planStatus.toUpperCase()}
                    </Tag>
                   
                  </div>
                </div>

                {/* Stats row */}
                <div className="pv2-sub-stats">
               
                  <div className="pv2-sub-stat-divider" />
                  <div className="pv2-sub-stat">
                    <span className="pv2-sub-stat-label">Next Billing</span>
                    <span className="pv2-sub-stat-value">
                      {profileValues.planEndDate ? dayjs(profileValues.planEndDate).format('DD MMM YYYY') : '—'}
                    </span>
                  </div>
                  <div className="pv2-sub-stat-divider" />
                  <div className="pv2-sub-stat">
                    <span className="pv2-sub-stat-label">Amount Paid</span>
                    <span className="pv2-sub-stat-value">₹{profileValues.amountPaid}</span>
                  </div>
                  <div className="pv2-sub-stat-divider" />
                  <div className="pv2-sub-stat">
                    <span className="pv2-sub-stat-label">Branch Limit</span>
                    <span className="pv2-sub-stat-value">{profileValues.branchLimit}</span>
                  </div>
                </div>

                {/* Progress */}
                {profileValues.planStartDate && profileValues.planEndDate && (
                  <div className="pv2-sub-progress">
                    <div className="pv2-sub-progress-labels">
                      <span className="pv2-sub-stat-label">Cycle Progress</span>
                      <span className="pv2-sub-stat-value">{subProgress}%</span>
                    </div>
                    <Progress
                      percent={subProgress}
                      showInfo={false}
                      strokeColor={{ from: 'var(--primary-color)', to: '#60a5fa' }}
                      trailColor="#f0f1f4"
                      strokeWidth={6}
                    />
                  </div>
                )}

              
              </div>
            </SectionCard>
          )}

        </div>
      )}
    </div>
  );
};

export default Profile;